/* globals ucCommonLogin */
import {ucDomain, ucAppID, passDomain, ucReg, getRouteQuery} from '@/common/util';
import CheckProtocol from '@/component/CheckProtocol';
import {parse as parseQuery} from 'qs';
import Vue from 'vue';

function checkProtocolDom(dom) {
    if (dom == null) {
        return;
    }
    const bindComponent = document.createElement('div');
    bindComponent.setAttribute('id', 'loginV3Checker');
    dom.appendChild(bindComponent);

    return new Vue({
        render: h => h(CheckProtocol)
    }).$mount('#loginV3Checker');
}

function handleCloseLoginLayer() {
    document.body.classList.remove('is-login-show');
    ucCommonLogin.hide();
}

// [QD-CUSTOM] 给登录弹窗自带的关闭按钮追加一个独立的 class 和点击监听，
// 方便单独处理"关闭弹窗时同步清理 body 状态"这类与登录 SDK 无关的逻辑。
// 用 addEventListener 追加监听，不覆盖 SDK 自身已绑定的关闭行为；
// rendered 在弹窗重新渲染（切换登录方式、刷新验证码等）时可能多次触发，
// 且关闭按钮节点在多次渲染间通常是同一个 DOM 节点，用 dataset 标记避免重复绑定。
function bindCloseLoginLayer() {
    const closeDom = document.querySelector('#close-common-login-layer');
    if (closeDom && !closeDom.dataset.qdCloseBound) {
        closeDom.dataset.qdCloseBound = 'true';
        closeDom.classList.add('qd-login-close-btn');
        closeDom.addEventListener('click', handleCloseLoginLayer);
    }
    // [QD-CUSTOM] 点击遮罩空白处也触发和关闭按钮完全一致的关闭效果（清 body 状态 + 隐藏弹窗）。
    // 用 event.target === maskDom 判断，只在真正点在遮罩本身（弹窗外的空白区域）时才关闭，
    // 点击遮罩内部的登录框会冒泡到 mask 节点，但 target 是登录框内部元素，不会误触发。
    const maskDom = document.querySelector('#uc-common-login-mask');
    if (maskDom && !maskDom.dataset.qdMaskBound) {
        maskDom.dataset.qdMaskBound = 'true';
        maskDom.addEventListener('click', event => {
            if (event.target === maskDom) {
                handleCloseLoginLayer();
            }
        });
    }
}

/**
 * 创建擎舵 LoginV3 页面的登录能力方法集合，与 Vue 组件解耦，
 * 仅依赖外部传入的容器 id 和 toast 实例，不挂载 mixin。
 * @param {Object} options
 * @param {string} options.containerId 登录组件挂载容器的 DOM id
 * @param {Object} options.toast 提示组件实例（this.$toast）
 * @param {Function} [options.onReady] SDK 首次 rendered 完成后的回调，用于通知外部"登录弹窗
 * 已可用"，避免用户在 SDK 渲染完成前点击登录按钮导致 show() 无实际弹窗可显示
 * @returns {{initLogin: Function, show: Function}}
 */
export function createPassportLogin({containerId, toast, onReady}) {
    let isReady = false;
    function initLogin() {
        const {origin} = window.location;
        const fromU = encodeURIComponent(origin);
        const loginDom = document.querySelector(`#${containerId}`);
        if (!loginDom) {
            return false;
        }
        ucCommonLogin.init({
            appid: ucAppID,
            staticPage: `${origin}/static/v3Jump.html`,
            multiLogin: 1,
            container: containerId,
            defaultLogin: 'uc',
            isCanCodeLogin: true,
            product: 'www2',
            defaultCss: 1,
            defaultCssVersion: 3,
            passUseOptions: {
                loginVersion: 'v4'
            },
            passLoginOptions: {
                sms: 5,
                userPwdLogin: 1
            },
            isLayer: 1,
            proxyUc: true,
            registerUcLink: `${ucReg}/?tpl=www2&fromu=${fromU}`,
            registerPassLink: `${passDomain}/v2/?reg&u=${fromU}`,
            success: opts => {
                const ucLogin = ucDomain;
                let href = '';
                const newPath = '/index';
                // 获取当前URL里的query参数【rurl】，如果有则直接取出；如果没有则按原先逻辑
                const routeQuery = getRouteQuery() || {};
                let rurl = routeQuery.rurl || '';
                // hairuo网关判断未登录后重定向到/login时，原目标地址被包在【fromu】参数里
                // （即main.do?rurl=xxx这个完整地址的encode），此时需要从fromu里解出真正的rurl
                if (rurl === '' && routeQuery.fromu) {
                    try {
                        const fromuDecoded = decodeURIComponent(routeQuery.fromu);
                        const fromuQueryStr = fromuDecoded.split('?')[1] || '';
                        const fromuQuery = parseQuery(fromuQueryStr) || {};
                        rurl = fromuQuery.rurl || '';
                    }
                    catch (e) {
                        rurl = '';
                    }
                }
                if (rurl === '') {
                    rurl = encodeURIComponent(newPath);
                }
                // 登录完成后默认都应该回到登录后的首页
                if (opts.type === 'passport') {
                    const hairuoMainUrl = `${origin}/hairuo/main.do?rurl=${rurl}`;
                    href = `${ucLogin}/?action=psUcLogin&appid=${ucAppID}&u=${encodeURIComponent(hairuoMainUrl)}`;
                }
                else {
                    href = `${origin}/hairuo/main.do?rurl=${rurl}`;
                }
                window.sessionStorage.removeItem('style_click_old');
                location.href = href;
            },
            rendered: item => {
                bindCloseLoginLayer();
                // [QD-CUSTOM] rendered 首次触发说明登录弹窗 DOM 已渲染完成，此时才通知外部
                // "登录能力已就绪"。避免用户在 SDK 异步渲染完成前点击登录按钮，
                // 此时 loginVisible 会被置为 true（蒙层/模糊态生效），但 show() 无实际
                // 弹窗内容可显示，导致蒙层卡住无法消失、也看不到登录框。
                if (!isReady) {
                    isReady = true;
                    onReady?.();
                }
                const {passportInstance} = item;
                if (!passportInstance) {
                    return;
                }
                passportInstance?.on('beforeSubmit', args => {
                    if (window.isCheckProtocol) {
                        args.returnValue = true;
                    }
                    else {
                        toast.warn('请先阅读并同意《擎舵平台用户服务协议》');
                        args.returnValue = false;
                    }
                });
                const dom1 = document.querySelector('#TANGRAM__PSP_4__submitWrapper');
                const dom2 = document.querySelector('#TANGRAM__PSP_4__smsSubmitWrapper');
                checkProtocolDom(dom1);
                checkProtocolDom(dom2);
            }
        });
        ucCommonLogin.hide();
        return true;
    }

    function show() {
        ucCommonLogin.show();
    }

    return {initLogin, show, isReady: () => isReady};
}
